import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import type { Setting } from '@/types';
import { Plus, Trash2 } from 'lucide-react';

type DepartmentItem = {
    value: string;
    label: string;
    roles: string[];
};

interface ProjectTaskDepartmentsEditorProps {
    settingKey: string;
    setting: Setting;
    availableRoles: string[];
    onUpdate: (key: string, value: DepartmentItem[]) => void;
}

function normalizeDepartments(value: unknown): DepartmentItem[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item) => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const record = item as Record<string, unknown>;
            const roles = Array.isArray(record.roles)
                ? record.roles.filter((role): role is string => typeof role === 'string' && role.trim() !== '')
                : [];

            return {
                value: typeof record.value === 'string' ? record.value : '',
                label: typeof record.label === 'string' ? record.label : '',
                roles,
            } satisfies DepartmentItem;
        })
        .filter((item): item is DepartmentItem => Boolean(item && item.value && item.label));
}

export function ProjectTaskDepartmentsEditor({ settingKey, setting, availableRoles, onUpdate }: ProjectTaskDepartmentsEditorProps) {
    const initialDepartments = useMemo(() => normalizeDepartments(setting.value), [setting.value]);
    const [departments, setDepartments] = useState<DepartmentItem[]>(initialDepartments);

    const sortedRoles = useMemo(() => [...availableRoles].sort((a, b) => a.localeCompare(b)), [availableRoles]);
    const hasChanges = JSON.stringify(departments) !== JSON.stringify(initialDepartments);

    const persistLocal = (next: DepartmentItem[]) => setDepartments(next);

    const addDepartment = () => {
        const index = departments.length + 1;
        persistLocal([
            ...departments,
            {
                value: `department_${index}`,
                label: `Department ${index}`,
                roles: [],
            },
        ]);
    };

    const removeDepartment = (index: number) => {
        persistLocal(departments.filter((_, i) => i !== index));
    };

    const patchDepartment = (index: number, patch: Partial<DepartmentItem>) => {
        const next = departments.map((row, i) => (i === index ? { ...row, ...patch } : row));
        persistLocal(next);
    };

    const toggleRole = (index: number, roleName: string, checked: boolean) => {
        const next = departments.map((row, i) => {
            if (i !== index) {
                return row;
            }

            const roles = checked ? Array.from(new Set([...row.roles, roleName])) : row.roles.filter((item) => item !== roleName);

            return {
                ...row,
                roles,
            };
        });

        persistLocal(next);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="space-y-1">
                    <div className="text-sm font-medium">Task departments</div>
                    <div className="text-muted-foreground text-xs">Define departments and allowed roles that can edit tasks.</div>
                </div>
                <Button type="button" variant="outline" onClick={addDepartment}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add department
                </Button>
            </div>
            <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" disabled={!hasChanges} onClick={() => setDepartments(initialDepartments)}>
                    Reset
                </Button>
                <Button type="button" disabled={!hasChanges} onClick={() => onUpdate(settingKey, departments)}>
                    Save
                </Button>
            </div>

            {departments.length === 0 ? (
                <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">No departments yet.</div>
            ) : (
                <div className="space-y-4">
                    {departments.map((department, index) => (
                        <Card key={`department-${index}`}>
                            <CardHeader className="space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                    <CardTitle className="text-base">{department.label || department.value}</CardTitle>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeDepartment(index)}
                                        title="Remove department"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label>Key</Label>
                                        <Input
                                            value={department.value}
                                            onChange={(e) => patchDepartment(index, { value: e.target.value })}
                                            placeholder="e.g., service_team"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Label</Label>
                                        <Input
                                            value={department.label}
                                            onChange={(e) => patchDepartment(index, { label: e.target.value })}
                                            placeholder="e.g., Service Team"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Roles allowed to edit tasks</Label>
                                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                        {sortedRoles.map((roleName) => {
                                            const checked = department.roles.includes(roleName);
                                            return (
                                                <label key={`${department.value}-${roleName}`} className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        className="rounded"
                                                        checked={checked}
                                                        onChange={(e) => toggleRole(index, roleName, e.target.checked)}
                                                    />
                                                    <span>{roleName}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
